import React, { useEffect } from "react"
import { io } from "socket.io-client"
import useSigninUserWithCredentials from "./signin-with-credentials"
import config from "aldoo-ra/Config"
import { showMessageDialog } from "aldoo-ra/MessageDialog"
const { API_URL } = config

// Providers whose OAuth result is written to localStorage by the callback page.
const PROVIDER_IDS = ["google", "facebook", "apple", "microsoft"]
const RESULT_TTL = 5 * 60 * 1000
// Per-tab flag marking that THIS tab actually started an OAuth attempt. The
// startup drain only auto-completes a result when the flag is set, so a stale
// `oauth-result-*` left in localStorage can't silently re-fire signin on an
// unrelated page load (the loop symptom). sessionStorage survives same-tab
// redirect/reload/iOS-tab-restore, so the legit mobile recovery still works.
const INFLIGHT_PREFIX = "oauth-inflight-"
const markInflight = (id) => { try { sessionStorage.setItem(INFLIGHT_PREFIX + id, "1") } catch {} }
const isInflight = (id) => { try { return !!sessionStorage.getItem(INFLIGHT_PREFIX + id) } catch { return false } }
const clearInflight = (id) => { try { sessionStorage.removeItem(INFLIGHT_PREFIX + id) } catch {} }


// Dev-only confirmation that we're about to sign in with the canned mock
// credentials configured in MOCK_SIGN_IN_PROVIDERS. Uses the shared
// MessageDialog so it lives in the same system-alert layer as every other
// confirmation in the app (no custom DOM, no bespoke z-index management).
const showMockProviderDialog = ({ provider, mockData, onCompleted }) => {
    showMessageDialog({
        variant: "info",
        title: `Mock ${provider.id} sign-in`,
        message: React.createElement(
            "pre",
            { className: "bg-neutral_1 rounded-lg p-3 text-xs overflow-auto max-h-[40vh] whitespace-pre-wrap" },
            JSON.stringify(mockData, null, 2)
        ),
        btnLabel: "OK — sign in with these credentials",
        showCloseX: false,
        onConfirm: () => onCompleted({ ...mockData }),
    })
}

const startProviderAuth = ({ provider, onCompleted }) => {
    //If this is an email provider, sign in with email
    if (provider.id === "email") return onCompleted({ email: provider.data })

    // Check for mock credentials in config (DEV only)
    const mockData = config.MOCK_SIGN_IN_PROVIDERS?.[provider.id.toUpperCase()]
    if (mockData) return showMockProviderDialog({ provider, mockData, onCompleted })

    // Correlation id that round-trips through the OAuth `state` param.
    // Lets the server push the result to this client via a socket room, without
    // relying on the session cookie or window.opener — both severed in the
    // partitioned OAuth popup on modern mobile browsers (iOS 17+/26).
    const nonce =
        (typeof crypto !== "undefined" && crypto.randomUUID && crypto.randomUUID()) ||
        `${provider.id}-${Date.now()}-${Math.random().toString(36).slice(2)}`

    const authUrl = `${API_URL}/${provider.id}?nonce=${encodeURIComponent(nonce)}`
    const expectedOrigin = API_URL || (typeof window !== "undefined" ? window.location.origin : "")

    // Mark this tab as having started OAuth so the startup drain will accept the result.
    markInflight(provider.id)

    // Mobile: popups open as a separate tab and the app tab is discarded/suspended
    // by iOS, so the opener never receives the result. Use a same-tab redirect; the
    // callback returns to the app, which drains the result from localStorage on load.
    const isTouch =
        typeof window !== "undefined" &&
        window.matchMedia?.("(pointer: coarse)")?.matches
    if (isTouch) {
        window.location.href = authUrl
        return
    }

    // Desktop shell: run OAuth in the SYSTEM browser — passkeys, password
    // managers and existing Google sessions live there, none of which work
    // reliably inside an embedded window (Google's passkey step hangs).
    // The nonce socket room pushes the result back to the app, so no
    // popup/opener is needed; older shells without openExternal fall
    // through to the popup flow below.
    const desktop = typeof window !== "undefined" ? window.aldooDesktop : null
    if (desktop?.openExternal) {
        // WebSocket only: the API runs as a cluster and refuses HTTP long-polling
        // ("Transport unknown"); socket.io's default of polling first never connects.
        const socket = io(API_URL, { transports: ["websocket"] })
        let finished = false
        const finish = (user) => {
            if (finished) return
            finished = true
            clearTimeout(timeout)
            clearInflight(provider.id)
            socket.disconnect()
            try { desktop.focusWindow?.() } catch {}
            onCompleted(user)
        }
        const timeout = setTimeout(() => finish(false), 5 * 60 * 1000)
        socket.on(`${provider.id}`, (user) => finish(user))
        const subscribe = () => socket.emit("oauth:subscribe", { nonce })
        socket.on("connect", subscribe)
        if (socket.connected) subscribe()
        desktop.openExternal(authUrl)
        return
    }

    const openPopup = () => {
        const width = 600
        const height = 700
        const left = window.innerWidth / 2 - width / 2
        const top = window.innerHeight / 2 - height / 2

        // Navigate to the auth URL synchronously, inside the click gesture.
        // A deferred popup.location.href is blocked by iOS 17+/26 (nav outside gesture).
        return window.open(
            authUrl,
            "",
            `toolbar=no, location=no, directories=no, status=no, menubar=no,
      scrollbars=no, resizable=no, copyhistory=no, width=${width},
      height=${height}, top=${top}, left=${left}`
        )
    }

    const socket = io(API_URL, { transports: ["websocket"] })
    const popup = openPopup()
    let finished = false
    const storageKey = `oauth-result-${provider.id}`

    // Popup blocked (common in iOS in-app browsers) — redirect the current page instead.
    if (!popup) {
        socket.disconnect()
        window.location.href = authUrl
        return
    }

    const finish = (user) => {
        if (finished) return
        finished = true
        clearTimeout(timeout)
        clearInterval(storagePoll)
        window.removeEventListener('message', handleMessage)
        try { localStorage.removeItem(storageKey) } catch {}
        clearInflight(provider.id)
        popup?.close()
        socket.disconnect()
        onCompleted(user)
    }

    // Timeout: cancel if no auth event within 5 minutes
    const timeout = setTimeout(() => finish(false), 5 * 60 * 1000)

    // Primary on mobile: socket room keyed by nonce (server push, immune to COOP
    // opener severance and storage partitioning).
    socket.on(`${provider.id}`, (user) => {
        console.log(`[OAuth] ${provider.id} socket event received`, user ? { name: user.name, email: user.email, hasGoogleId: !!user.google_id } : 'NO USER DATA')
        finish(user)
    })
    const subscribe = () => socket.emit("oauth:subscribe", { nonce })
    // Re-subscribe on every (re)connect; also subscribe now if the socket is
    // already connected (reused warm connection won't re-fire "connect").
    socket.on("connect", subscribe)
    if (socket.connected) subscribe()

    // Fallback: postMessage from popup (works when window.opener survives).
    const handleMessage = (event) => {
        if (expectedOrigin && event.origin !== expectedOrigin) return
        if (event.data?.type !== `${provider.id}-auth-result`) return
        console.log(`[OAuth] ${provider.id} postMessage received`, event.data.user ? { name: event.data.user.name, email: event.data.user.email } : 'NO USER DATA')
        finish(event.data.user || false)
    }
    window.addEventListener('message', handleMessage)

    // Fallback: localStorage polling (works when storage isn't partitioned).
    try { localStorage.removeItem(storageKey) } catch {}
    const storagePoll = setInterval(() => {
        try {
            const item = JSON.parse(localStorage.getItem(storageKey))
            if (item && Date.now() - item.ts < 60000) {
                console.log(`[OAuth] ${provider.id} localStorage result received`, item.user ? { name: item.user.name, email: item.user.email } : 'NO USER DATA')
                finish(item.user || false)
            }
        } catch {}
    }, 500)
}

const completeSignin = (user, provider, signinUser) => {
    if (!user) return
    if (provider?.require) user.require = provider.require
    // Map provider picture fields to avatar
    if (!user.avatar) user.avatar = user.picture || user.image
    signinUser(user)
}

const useSigninWithProvider = () => {
    const { state, error, signinUser } = useSigninUserWithCredentials()

    // Recover a result that arrived after the opener tab was reloaded by iOS.
    // The callback page persists the result to localStorage; on iOS the original
    // tab is often discarded while the OAuth tab is foregrounded, destroying the
    // in-flight listeners. We drain any fresh result on startup, and also listen
    // for the cross-tab `storage` event in case the popup writes while we wait.
    useEffect(() => {
        const tryProvider = (id) => {
            const key = `oauth-result-${id}`
            let item
            try { item = JSON.parse(localStorage.getItem(key)) } catch { return false }
            if (!item) return false
            // Only auto-complete a result this tab actually initiated. A leftover
            // result with no in-flight flag is stale — purge it, don't sign in with it.
            if (!isInflight(id)) {
                try { localStorage.removeItem(key) } catch {}
                return false
            }
            try { localStorage.removeItem(key) } catch {}
            clearInflight(id)
            if (item.user && Date.now() - item.ts < RESULT_TTL) {
                console.log(`[OAuth] ${id} recovered from localStorage`)
                completeSignin(item.user, { id }, signinUser)
                return true
            }
            return false
        }

        for (const id of PROVIDER_IDS) {
            if (tryProvider(id)) break
        }

        const onStorage = (e) => {
            if (!e.key || !e.key.startsWith("oauth-result-") || !e.newValue) return
            tryProvider(e.key.slice("oauth-result-".length))
        }
        window.addEventListener("storage", onStorage)
        return () => window.removeEventListener("storage", onStorage)
    }, [])

    const signin = async function (provider) {
        //3rd party provider, start the auth process
        startProviderAuth({
            provider,
            onCompleted: (user) => completeSignin(user, provider, signinUser),
        })
    }
    return { state, error, signin }
}

export default useSigninWithProvider